"use client";

import { api, RouterOutputs } from "@/trpc/react";
import { cn } from "@workspace/ui/lib/utils";
import { ExternalLink } from "lucide-react";
import NextLink from "next/link";
import type { Platform } from "./links-platform-content";
import { FeaturedLink } from "@/components/public/featured-link";
import { captureEvent } from "@/components/analytics";

interface LinksMarkdownViewProps {
  platform?: Platform;
}

type PublicLink = RouterOutputs['link']['getAll'][number];

export function LinksMarkdownView({ platform = 'default' }: LinksMarkdownViewProps) {
  const { data: categories, isLoading: categoriesLoading } = api.category.getAll.useQuery();
  const { data: links, isLoading: linksLoading } = api.link.getAll.useQuery();
  
  // Helper function to get the appropriate URL based on platform
  const getPlatformUrl = (link: PublicLink) => {
    switch (platform) {
      case 'instagram':
        return link.instagramUrl || link.url;
      case 'tiktok':
        return link.tiktokUrl || link.url;
      default:
        return link.url;
    }
  };

  const isInternal = (href: string | undefined | null) => !!href && href.startsWith('/') && !href.startsWith('//');

  // Stable identity for PostHog, which autocapture's link text and href lack:
  // those change whenever a link is renamed or repointed. A featured link can
  // also sit in its category list, so `placement` records which one was used.
  const trackClick = (
    link: PublicLink,
    href: string,
    placement: 'featured' | 'list',
    category: string | null,
  ) =>
    captureEvent('link_clicked', {
      link_id: link.id,
      link_name: link.name,
      link_url: href,
      placement,
      category,
      platform: platform === 'default' ? 'web' : platform,
    });
  
  const isLoading = categoriesLoading || linksLoading;
  
  if (isLoading || !categories || !links) {
    return (
      <div className="min-h-screen bg-background">
        <div className="public-width py-12">
          <div className="animate-pulse space-y-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="space-y-3">
                <div className="h-8 bg-muted rounded w-48" />
                <div className="space-y-2">
                  <div className="h-4 bg-muted rounded w-full" />
                  <div className="h-4 bg-muted rounded w-3/4" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }
  
  // Featured links (no category, highlighted at top)
  const featuredLinks = links.filter((l) => l.featured);

  // Group links by category
  const categoriesWithLinks = categories
    .map(category => ({
      ...category,
      links: links
        .filter(link => link.categoryId === category.id)
        .sort((a, b) => a.order - b.order),
    }))
    .filter(cat => cat.links.length > 0)
    .sort((a, b) => a.order - b.order);
  
  
  return (
    <div className="min-h-screen bg-background">
      {/* Simple header */}
      <div className="public-width py-12">
        <header className="mb-4">
          <h1 className="text-3xl font-bold mb-2">Links</h1>
          <p className="text-muted-foreground">A curated collection of resources and tools.</p>
        </header>
        
        {/* Featured links */}
        {featuredLinks.length > 0 && (
          <div className="mb-8 space-y-3">
            {featuredLinks.map((fl) => (
              <FeaturedLink
                key={fl.id}
                title={fl.name}
                description={fl.description || undefined}
                href={getPlatformUrl(fl)}
                onClick={() => trackClick(fl, getPlatformUrl(fl), 'featured', null)}
              />
            ))}
          </div>
        )}

        {/* Markdown-style content */}
        <div className="max-w-none">
          {categoriesWithLinks.map((category, categoryIndex) => (
            <div key={category.id} className={cn(
              "mb-6",
              categoryIndex > 0 && "pt-4 border-t border-border"
            )}>
              {/* Category heading */}
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                {category.name}
              </h2>
              
              {/* Links list */}
              <ul className="space-y-2 list-none pl-0">
                {category.links.map(link => {
                  const href = getPlatformUrl(link);
                  const internal = isInternal(href);
                  return (
                    <li key={link.id} className="group">
                      {internal ? (
                        <NextLink
                          href={href}
                          onClick={() => trackClick(link, href, 'list', category.name)}
                          className={cn(
                            "block",
                            "transition-colors"
                          )}
                        >
                          <div className="inline-flex items-center gap-2 text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60">
                            <span className="font-medium">{link.name}</span>
                            <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                          </div>
                          {link.description ? (
                            <p className="mt-1 text-sm text-muted-foreground">{link.description}</p>
                          ) : null}
                        </NextLink>
                      ) : (
                        <a
                          href={href}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => trackClick(link, href, 'list', category.name)}
                          className={cn("block", "transition-colors")}
                        >
                          <div className="inline-flex items-center gap-2 text-primary hover:text-primary/80 underline decoration-primary/30 hover:decoration-primary/60">
                            <span className="font-medium">{link.name}</span>
                            <ExternalLink className="w-3 h-3 opacity-50 group-hover:opacity-100" />
                          </div>
                          {link.description ? (
                            <p className="mt-1 text-sm text-muted-foreground">{link.description}</p>
                          ) : null}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
